import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, CheckCircle, Cloud } from 'lucide-react';
import { Float, Magnetic, Shimmer } from './Motion';

const platforms = [
  {
    short: 'CPA',
    full: 'Caribbean Payments Alliance',
    angle: 0,
    label: 'Payments',
    flagship: true,
    logo: '/logos/CPALogo.png',
    url: 'https://caribbeanpaymentsalliance.com',
    desc: 'Payment orchestration, QuickPay, eStores, merchant onboarding, and multi-rail payment infrastructure.',
  },
  {
    short: 'CSP',
    full: 'CloudSync Pro',
    angle: 90,
    label: 'Hospitality',
    logo: '/logos/CloudSyncPro.png',
    icon: Cloud,
    url: 'https://csyncpro.com',
    desc: 'Cloudbeds and local bank payment integrations for hotels and hospitality businesses.',
  },
  {
    short: 'BP',
    full: 'BahamasPay',
    angle: 180,
    label: 'Business Formation',
    logo: '/logos/BahamasPay.png',
    url: 'https://bahamaspay.com',
    desc: 'Business incorporation, licensing, IBC services, and digital business formation support.',
  },
  {
    short: 'APC',
    full: 'APC Estate Flow',
    angle: 270,
    label: 'Real Estate',
    logo: '/logos/APCEstateFlow.png',
    url: 'https://apcestateflow.com',
    desc: 'Real estate, estate-management, probate, and transaction workflow solutions.',
  },
];

const trustPoints = [
  'Multi-platform digital ecosystem',
  'Payment & eCommerce infrastructure',
  'Caribbean-rooted, globally positioned',
  'Built for merchants, institutions, and emerging markets',
];

const CorporateEcosystem = () => {
  const [active, setActive] = useState(null);
  const radius = 220;

  return (
    <section
      id="ecosystem-visual"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      data-testid="corporate-ecosystem"
    >
      {/* Subtle off-white gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#fafaf7] to-white" />
      {/* Faint gold radial */}
      <motion.div
        className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full blur-3xl opacity-[0.06]"
        style={{ background: 'radial-gradient(circle, #C9A227 0%, transparent 70%)' }}
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <span className="inline-flex items-center gap-3 mb-4">
            <div className="h-[1px] w-8 bg-[#C9A227]" />
            <span className="text-[#C9A227] text-[10px] tracking-[0.4em] uppercase font-semibold">Unified Infrastructure</span>
            <div className="h-[1px] w-8 bg-[#C9A227]" />
          </span>
          <h2
            className="text-[#0a0a0a] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-5 leading-[1.1]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            The <span className="text-[#C9A227] italic">Sebastian Alliance Group</span> Ecosystem
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-3xl mx-auto font-light leading-relaxed">
            A corporate technology group powering payments, eCommerce, hospitality, AI, real estate,
            and business infrastructure across the Caribbean and global markets.
          </p>
        </motion.div>

        {/* Diagram + Summary panel side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* LEFT — Diagram */}
          <div className="lg:col-span-7 relative h-[680px] hidden md:flex items-center justify-center">
            {/* Orbit rings */}
            <motion.div
              className="absolute w-[480px] h-[480px] rounded-full border border-[#C9A227]/25"
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div className="absolute w-[400px] h-[400px] rounded-full border border-[#0a0a0a]/5" />
            <div className="absolute w-[540px] h-[540px] rounded-full border border-[#C9A227]/12" />

            {/* SVG lines with labels */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
              {platforms.map((p, i) => {
                const rad = (p.angle * Math.PI) / 180;
                const x = Math.cos(rad) * radius;
                const y = Math.sin(rad) * radius;
                const labelX = Math.cos(rad) * (radius * 0.55);
                const labelY = Math.sin(rad) * (radius * 0.55);
                return (
                  <g key={i}>
                    <motion.line
                      x1="50%"
                      y1="50%"
                      x2={`calc(50% + ${x}px)`}
                      y2={`calc(50% + ${y}px)`}
                      stroke={active === i ? 'rgba(201,162,39,0.9)' : 'rgba(201,162,39,0.5)'}
                      strokeWidth={active === i ? 2 : 1}
                      strokeDasharray="4 4"
                      initial={{ pathLength: 0, opacity: 0 }}
                      whileInView={{ pathLength: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.3 + i * 0.1 }}
                    />
                    <motion.text
                      x={`calc(50% + ${labelX}px)`}
                      y={`calc(50% + ${labelY}px)`}
                      textAnchor="middle"
                      className="fill-[#C9A227]"
                      style={{ fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700 }}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.8 + i * 0.1 }}
                    >
                      {p.label}
                    </motion.text>
                  </g>
                );
              })}
            </svg>

            {/* Center SAG — gold disk with continuous float */}
            <Float distance={6} duration={6}>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, type: 'spring' }}
                whileHover={{ scale: 1.05 }}
                className="relative z-20 w-40 h-40 rounded-full bg-gradient-to-br from-[#C9A227] to-[#a8861e] flex flex-col items-center justify-center cursor-default"
                style={{ boxShadow: '0 12px 48px -8px rgba(201,162,39,0.45)' }}
              >
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{ boxShadow: '0 0 60px rgba(201,162,39,0.45)' }}
                  animate={{ opacity: [0.4, 0.9, 0.4], scale: [1, 1.08, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <img src="/logos/sag-logo.png" alt="SAG" className="relative w-14 h-auto mb-1" />
                <div className="relative text-[#0a0a0a] text-[9px] tracking-[0.2em] uppercase font-bold text-center px-2 leading-tight">Sebastian Alliance<br />Group LLC</div>
              </motion.div>
            </Float>

            {/* Platform nodes — TRANSPARENT background, just the logo */}
            {platforms.map((p, i) => {
              const rad = (p.angle * Math.PI) / 180;
              const x = Math.cos(rad) * radius;
              const y = Math.sin(rad) * radius;
              const isFlagship = p.flagship;
              const size = isFlagship ? 'w-32 h-32' : 'w-28 h-28';
              return (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 + i * 0.08, type: 'spring' }}
                  className="absolute z-10"
                  style={{
                    left: `calc(50% + ${x}px)`,
                    top: `calc(50% + ${y}px)`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => setActive(active === i ? null : i)}
                >
                  <div className="group relative cursor-pointer">
                    {isFlagship && (
                      <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#C9A227] text-[#0a0a0a] text-[8px] tracking-wider uppercase font-bold whitespace-nowrap">
                        Flagship
                      </div>
                    )}
                    <Float distance={4} duration={3 + i * 0.5} delay={i * 0.3}>
                      <motion.div
                        className={`${size} rounded-full flex items-center justify-center transition-all p-3 ${
                          isFlagship
                            ? 'ring-2 ring-[#C9A227] ring-offset-4 ring-offset-white'
                            : 'ring-1 ring-[#C9A227]/30 ring-offset-2 ring-offset-white hover:ring-[#C9A227]'
                        }`}
                        animate={active === i ? { scale: 1.12 } : { scale: 1 }}
                        whileHover={{ rotateY: 12, scale: 1.08 }}
                        transition={{ type: 'spring', stiffness: 220, damping: 18 }}
                        style={{ transformStyle: 'preserve-3d' }}
                      >
                        {p.logo ? (
                          <img src={p.logo} alt={p.full} className="max-w-full max-h-full object-contain" />
                        ) : p.icon ? (
                          <p.icon className="w-10 h-10 text-[#0a0a0a]" />
                        ) : (
                          <span className="text-[#0a0a0a] text-base font-bold tracking-wider">{p.short}</span>
                        )}
                      </motion.div>
                    </Float>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 text-[#0a0a0a] text-[10px] tracking-wider uppercase whitespace-nowrap font-semibold">
                      {p.full}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* RIGHT — Summary panel — LIGHT theme */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-5"
          >
            <div className="bg-white border border-gray-200 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.18)] p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-[1px] w-6 bg-[#C9A227]" />
                <span className="text-[#C9A227] text-[9px] tracking-[0.3em] uppercase font-bold">Corporate Overview</span>
              </div>
              <h3 className="text-[#0a0a0a] text-2xl md:text-3xl font-light mb-4 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Sebastian Alliance Group LLC
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-7">
                A digital infrastructure and fintech innovation group building platforms that support
                merchants, financial institutions, hotels, real estate professionals, and entrepreneurs.
              </p>

              {/* Trust points */}
              <ul className="space-y-3 mb-7">
                {trustPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-gray-700 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>

              {/* Active platform description (hover-driven) */}
              {active !== null ? (
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-[#fafaf7] border-l-2 border-[#C9A227] pl-4 py-3 mb-6"
                  data-testid="active-platform-panel"
                >
                  <div className="text-[#C9A227] text-[10px] tracking-[0.2em] uppercase font-bold mb-1">{platforms[active].short} — {platforms[active].full}</div>
                  <p className="text-gray-600 text-xs leading-relaxed mb-3">{platforms[active].desc}</p>
                  <a
                    href={platforms[active].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#C9A227] hover:text-[#0a0a0a] text-[10px] tracking-[0.2em] uppercase font-bold transition-colors"
                  >
                    Explore {platforms[active].short}
                    <ArrowRight className="w-3 h-3" />
                  </a>
                </motion.div>
              ) : (
                <p className="text-gray-400 text-[11px] tracking-wider italic mb-6">
                  Hover or tap any platform circle to learn more.
                </p>
              )}

              {/* CTA buttons */}
              <div className="grid grid-cols-1 gap-2.5 pt-5 border-t border-gray-200">
                <a
                  href="#solutions"
                  className="group inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#C9A227] text-[#0a0a0a] text-[10px] tracking-[0.2em] uppercase font-bold hover:bg-[#0a0a0a] hover:text-white transition-all"
                  data-testid="panel-explore-platforms"
                >
                  Explore Our Platforms
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href="#cta-consult"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#0a0a0a]/20 text-[#0a0a0a] text-[10px] tracking-[0.2em] uppercase font-medium hover:border-[#C9A227] hover:text-[#C9A227] transition-all"
                  data-testid="panel-schedule-consultation"
                >
                  <Calendar className="w-3 h-3" />
                  Schedule a Consultation
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mobile platform list */}
        <div className="md:hidden grid grid-cols-1 gap-3 mt-8">
          {platforms.map((p) => (
            <a
              key={p.short}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-gray-200 hover:border-[#C9A227] p-4 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[#C9A227] text-base font-bold tracking-wider">{p.short}</span>
                <span className="text-gray-400 text-[9px] tracking-wider uppercase">{p.label}</span>
              </div>
              <div className="text-[#0a0a0a] text-sm font-medium mb-2">{p.full}</div>
              <p className="text-gray-500 text-xs leading-relaxed">{p.desc}</p>
            </a>
          ))}
        </div>

        {/* Mini trust strip below ecosystem */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-20 pt-12 border-t border-gray-200"
        >
          <p className="text-center text-gray-400 text-[11px] tracking-[0.3em] uppercase font-medium mb-8">
            Integrated and aligned with leading commerce, payment &amp; technology platforms
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 max-w-5xl mx-auto">
            {['Visa', 'Scotiabank', 'RBC', 'Bank of The Bahamas', 'SunCash', "Plug'n Pay", 'Cloudbeds', 'WooCommerce', 'Shopify', 'WordPress', 'Wix', 'Joomla'].map((name) => (
              <span key={name} className="text-gray-400 hover:text-[#C9A227] text-sm font-medium tracking-wide transition-colors cursor-default">
                {name}
              </span>
            ))}
          </div>
          <p className="text-center text-gray-400 text-[10px] tracking-wider mt-6 max-w-3xl mx-auto italic px-4">
            Embeds into Shopify storefronts and external web applications where applicable. Connects with
            Wix, WordPress, Joomla, and standard websites through payment links, embedded checkout,
            custom integrations, and QuickPay tools.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default CorporateEcosystem;
